import { type JSX, memo, useState } from "react";
import PlusMinus from "../../shared/components/icons/PlusMinus";
import TaskIcon from "../../shared/components/icons/TaskIcon";
import { formatYMD, parseDate } from "../../shared/dateFns";
import t from "../../shared/translations";
import DownloadAnswers from "../myAnswers/DownloadAnswers";
import type { ICurrentPath } from "./MyHistory";
import {
    AccordionBody,
    AccordionContainer,
    AccordionHeader,
    AccordionHeading,
    Button,
    DatesHolder,
    SectionHeading,
    Stack,
    TaskAnchor,
    TaskTable,
} from "./stylings";

interface IProps {
    currentPaths: ICurrentPath[];
}

function CurrentPaths({ currentPaths }: IProps): JSX.Element {
    const [open, setOpen] = useState("");
    const toggle = (id: string) => {
        setOpen((currId) => (currId === id ? "" : id));
    };

    if (currentPaths.length === 0) {
        return null;
    }

    return (
        <section>
            <SectionHeading>{t("player.my-history.heading-current")}</SectionHeading>
            <div>
                {currentPaths.map((path) => (
                    <AccordionContainer key={path.id}>
                        <AccordionHeader onClick={() => toggle(path.id)} $pointer={true}>
                            <AccordionHeading>{path.pathname}</AccordionHeading>
                            <div>
                                {path.acceptedItems.reduce((acc, item) => acc + item.earnedMiles, 0)}/{path.goal}
                            </div>
                            <PlusMinus open={open === path.id} />
                        </AccordionHeader>
                        {open === path.id && (
                            <AccordionBody>
                                <DatesHolder>
                                    <div>{t("player.my-history.label-start-date", { date: path.startDate })}</div>
                                    <div>{t("player.my-history.label-end-date", { date: path.endDate })}</div>
                                </DatesHolder>
                                {path.acceptedItems.length > 0 && (
                                    <>
                                        <AccordionHeading>{t("player.my-history.heading-done-tasks")}</AccordionHeading>
                                        <TaskTable>
                                            <thead>
                                                <tr>
                                                    <th>{t("player.my-history.table-tasks-heading-task")}</th>
                                                    <th>{t("player.my-history.table-tasks-heading-finish-date")}</th>
                                                    <th>{t("player.my-history.table-tasks-heading-miles")}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {path.acceptedItems
                                                    .filter((i) => i.type !== "event")
                                                    .map((item) => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <TaskAnchor href={item.url}>
                                                                    <TaskIcon icon={item.icon} active={true} />
                                                                    {item.name}
                                                                </TaskAnchor>
                                                            </td>
                                                            <td>{formatYMD(parseDate(item.finishDatetime))}</td>
                                                            <td>{item.earnedMiles}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </TaskTable>
                                    </>
                                )}
                                <Stack>
                                    <form action="/select-path" method="POST">
                                        <input name="_csrf_token" type="hidden" value={__CSRF_TOKEN__} />
                                        <input type="hidden" name="tool" value="path" />
                                        <input type="hidden" name="path_id" value={path.id} />
                                        <Button type="submit">{t("player.my-history.button-view")}</Button>
                                    </form>
                                    <DownloadAnswers pathId={path.id} />
                                </Stack>
                            </AccordionBody>
                        )}
                    </AccordionContainer>
                ))}
            </div>
        </section>
    );
}

export default memo(CurrentPaths);
